import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * What asking for a cash receipt's portal fetch did. Only `requested`
 * publishes AR_REMITTANCE_PORTAL_FETCH_REQUESTED; every other outcome says
 * why nothing was asked for.
 */
export const REMITTANCE_PORTAL_FETCH_REQUEST_OUTCOMES = [
  /** Published; the customer's portal fetch workflow starts. */
  "requested",
  /** A fetch was already asked for this receipt and customer. */
  "already_requested",
  /** The receipt has no customer yet. */
  "no_customer",
  /** The receipt's customer does not send its remittances on a portal. */
  "not_portal_customer",
  /** The receipt already has a remittance linked. */
  "already_explained",
  /** No active portal fetch workflow listens for this customer. */
  "no_portal_workflow",
] as const;

export type RemittancePortalFetchRequestOutcome =
  (typeof REMITTANCE_PORTAL_FETCH_REQUEST_OUTCOMES)[number];

/**
 * Request Remittance Portal Fetch
 *
 * Asks the customer's "AR Remittance Portal Fetch" workflow to download this
 * cash receipt's remittance from the customer's portal. Requests at most once
 * per receipt and customer, and only for a receipt whose customer is a portal
 * customer, that has no remittance linked yet and whose customer has a fetch
 * workflow set up; otherwise it requests nothing and says why. The fetch
 * links the remittance to the receipt when it comes back.
 */
export const requestRemittancePortalFetchStep = defineStepConfig({
  type: "request_remittance_portal_fetch",
  name: "Request Remittance Portal Fetch",
  description:
    "Ask the customer's portal fetch workflow to download a cash receipt's remittance (once per receipt)",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    cashReceipt: entityOfType(SystemEntity.AR_CASH_RECEIPT)
      .optional()
      .describe(
        "The cash receipt entity reference (e.g. Get Cash Receipt's cashReceipt). Must reference a cash receipt.",
      ),
    cashReceiptId: z
      .uuid()
      .optional()
      .describe(
        "Bare cash receipt id — alternative to `cashReceipt`. Ignored when `cashReceipt` is wired.",
      ),
  }),

  outputSchema: z.object({
    outcome: z
      .enum(REMITTANCE_PORTAL_FETCH_REQUEST_OUTCOMES)
      .describe("Whether a fetch was requested, or why not"),
    requested: z.boolean().describe("True only when a fetch was requested"),
    customerId: z
      .uuid()
      .nullable()
      .describe("The receipt's customer, when it has one"),
    message: z.string().describe("The outcome in plain words"),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
